/**
 * PORTED — RO-3-00c / #44470.
 * Origin: `excellent/app/_modes/issues-detail-header.tsx`.
 *
 * Changes:
 *   `@/projects/db` counts/assignees → `useApi(modes.issuesChrome)` (READ)
 *   toolbar producer → local `IssuesTitleToolbar`
 */

import {
  IssuesStatusPills,
  SectionTitleBar,
  activeStatusSegment,
  resolveTasksState,
  type MetaFact,
} from "@excellent/ui";
import { Boxes, CircleCheck, ListTodo, Users } from "@excellent/ui/icons";
import { useApi } from "@excellent/ui/api";
import { modes } from "@excellent/ui/api/contracts/modes";
import { IssuesTitleToolbar } from "./issues-title-toolbar";

export function IssuesDetailHeader({
  searchParams,
  workspaceId,
  backHref,
  backLabel,
}: {
  searchParams: Record<string, string | string[] | undefined> | undefined;
  workspaceId: number;
  backHref: string;
  backLabel: string;
}) {
  const sp0 = searchParams ?? {};
  const sp =
    "status" in sp0 || "section" in sp0
      ? sp0
      : { ...sp0, status: "in_progress,review,todo,backlog", range: "all" };
  const state = resolveTasksState(sp);
  const projectSlug =
    typeof state.projectSlug === "string" ? state.projectSlug : undefined;

  const { data } = useApi(modes.issuesChrome, {
    query: projectSlug ? { projectSlug } : {},
  });

  const counts = data?.counts ?? {};
  const total = data?.total ?? 0;
  const done = data?.done ?? 0;
  const assigneeCount = data?.assigneeCount ?? 0;
  const scopeLabel = data?.scopeLabel ?? "All projects";
  const activeKey = activeStatusSegment(state.effectiveStatuses);

  const controls = (
    <IssuesTitleToolbar
      searchParams={searchParams}
      workspaceId={workspaceId}
      projectSlug={projectSlug}
    />
  );

  const facts: MetaFact[] = [
    { icon: <Boxes className="size-3.5" />, label: "Scope", value: scopeLabel },
    {
      icon: <ListTodo className="size-3.5" />,
      label: "Total",
      value: `${total} ${total === 1 ? "issue" : "issues"}`,
    },
    {
      icon: <Users className="size-3.5" />,
      label: "Assignees",
      value: String(assigneeCount),
    },
    {
      icon: <CircleCheck className="size-3.5" />,
      label: "Completion",
      value: total > 0 ? `${Math.round((done / total) * 100)}%` : "—",
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <SectionTitleBar
        back={{ label: backLabel, href: backHref }}
        title="Issues"
        description="Every issue across your projects — task-reviews, escalations, and launch gates, folded into one queue."
        meta={facts}
        actions={controls}
      />
      <IssuesStatusPills counts={counts} activeKey={activeKey} />
    </div>
  );
}

/** Alias matching the original export name used by mode-page. */
export const buildIssuesDetailHeader = IssuesDetailHeader;
